import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import config from '../config';
import {
  Autocomplete,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Typography,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Check as CheckIcon, Close as CloseIcon, Print as PrintIcon, Refresh as RefreshIcon, Lock as LockIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useConfirm } from '../hooks/useConfirm';
import { absencesService } from '../services/absences.service';
import employeesService from '../services/employees.service';
import { branchesService } from '../services/branches.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { useAppSelector } from '../hooks/redux.hooks';
import { Absence } from '../types/absences.types';
import { Employee } from '../types/employees.types';
import { Branch } from '../types/branches.types';
import AbsenceDialog from '../components/dialogs/AbsenceDialog';
import AttachmentSection from '../components/common/AttachmentSection';

const AbsencesPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const confirm = useConfirm();
  const user = useAppSelector((state) => state.auth.user);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const isSuperAdmin = user?.isSuperAdmin === true;

  const userBranchIds = JSON.parse(localStorage.getItem('userBranchIds') || '[]');
  const canEditAbsence = (branchId: number) => {
    return userBranchIds.length === 0 || userBranchIds.includes(branchId);
  };

  const [absences, setAbsences] = useState<Absence[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState<Absence | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  
  const [selectedBranch, setSelectedBranch] = useState<number | ''>('');
  const [selectedEmployee, setSelectedEmployee] = useState<number | ''>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const t = {
    title: isRTL ? 'إدارة الغياب' : 'Absences Management',
    addNew: isRTL ? 'إضافة غياب' : 'Add Absence',
    branch: isRTL ? 'الفرع' : 'Branch',
    employee: isRTL ? 'الموظف' : 'Employee',
    fromDate: isRTL ? 'من تاريخ' : 'From Date',
    toDate: isRTL ? 'إلى تاريخ' : 'To Date',
    status: isRTL ? 'الحالة' : 'Status',
    search: isRTL ? 'بحث' : 'Search',
    clear: isRTL ? 'مسح' : 'Clear',
    print: isRTL ? 'طباعة' : 'Print',
    absenceDate: isRTL ? 'تاريخ الغياب' : 'Absence Date',
    absenceType: isRTL ? 'نوع الغياب' : 'Absence Type',
    days: isRTL ? 'أيام' : 'Days',
    hours: isRTL ? 'ساعات' : 'Hours',
    deduction: isRTL ? 'الخصم' : 'Deduction',
    reason: isRTL ? 'السبب' : 'Reason',
    actions: isRTL ? 'الإجراءات' : 'Actions',
    pending: isRTL ? 'معلق' : 'Pending',
    approved: isRTL ? 'معتمد' : 'Approved',
    rejected: isRTL ? 'مرفوض' : 'Rejected',
    all: isRTL ? 'الكل' : 'All',
    loadError: isRTL ? 'خطأ في تحميل البيانات' : 'Error loading data',
    deleteSuccess: isRTL ? 'تم الحذف بنجاح' : 'Deleted successfully',
    deleteError: isRTL ? 'خطأ في الحذف' : 'Error deleting',
    approveSuccess: isRTL ? 'تمت الموافقة بنجاح' : 'Approved successfully',
    confirmDelete: isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?',
    totalDays: isRTL ? 'إجمالي الأيام' : 'Total Days',
    totalHours: isRTL ? 'إجمالي الساعات' : 'Total Hours',
    totalDeduction: isRTL ? 'إجمالي الخصم' : 'Total Deduction',
    reportTitle: isRTL ? 'تقرير الغياب' : 'Absences Report',
    count: isRTL ? 'العدد' : 'Count',
    noData: isRTL ? 'لا توجد بيانات' : 'No data found',
    applied: isRTL ? 'تم الخصم' : 'Applied',
    appliedTooltip: isRTL ? 'تم خصمه من الراتب — لا يمكن التعديل أو الحذف' : 'Deducted from payroll — cannot edit or delete'
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [absencesData, employeesData, branchesData, companyData] = await Promise.all([
        absencesService.getAll(),
        employeesService.getAll(),
        branchesService.getAll(),
        companyInfoService.getCompanyInfo().catch(() => null)
      ]);
      setAbsences(absencesData);
      setEmployees(employeesData.data || employeesData);
      setBranches(branchesData);
      setCompanyInfo(companyData);
    } catch (error) {
      toast.error(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const data = await absencesService.getAll({
        branchId: selectedBranch || undefined,
        employeeId: selectedEmployee || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        status: statusFilter || undefined
      });
      setAbsences(data);
    } catch (error) {
      toast.error(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ message: t.confirmDelete, severity: 'error' }))) return;
    try {
      await absencesService.delete(id);
      toast.success(t.deleteSuccess);
      loadData();
    } catch (error) {
      toast.error(t.deleteError);
    }
  };

  const handleAcknowledge = async (id: number) => {
    try {
      await absencesService.acknowledge(id);
      toast.success(isRTL ? 'تمت الموافقة الأولية' : 'Acknowledged');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error');
    }
  };

  const handleApprove = async (id: number, status: 'Approved' | 'Rejected') => {
    try {
      await absencesService.approve({ absenceId: id, status });
      toast.success(t.approveSuccess);
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.loadError);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Acknowledged': return 'warning';
      case 'Rejected': return 'error';
      case 'Pending': return 'warning';
      default: return 'default';
    }
  };

  const getAbsenceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      FullDay: isRTL ? 'يوم كامل' : 'Full Day',
      HalfDay: isRTL ? 'نصف يوم' : 'Half Day',
      LateHours: isRTL ? 'تأخير' : 'Late Hours',
      ExcusedAbsence: isRTL ? 'غياب بعذر' : 'Excused',
      UnexcusedAbsence: isRTL ? 'غياب بدون عذر' : 'Unexcused',
      SickLeave: isRTL ? 'إجازة مرضية' : 'Sick Leave',
      EmergencyLeave: isRTL ? 'إجازة طارئة' : 'Emergency Leave'
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      Pending: isRTL ? 'معلق' : 'Pending',
      Acknowledged: isRTL ? 'موافقة أولية' : 'Acknowledged',
      Approved: isRTL ? 'معتمد' : 'Approved',
      Rejected: isRTL ? 'مرفوض' : 'Rejected',
      Cancelled: isRTL ? 'ملغي' : 'Cancelled'
    };
    return labels[status] || status;
  };

  const getStatusPrintColor = (status: string) => {
    switch (status) {
      case 'Approved': return '#4caf50';
      case 'Rejected': return '#f44336';
      case 'Pending': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const handlePrint = () => {
    if (absences.length === 0) {
      toast.info(t.noData);
      return;
    }

    const totalDays = absences.reduce((sum, a) => sum + a.days, 0);
    const totalHours = absences.reduce((sum, a) => sum + a.hours, 0);
    const totalDeduction = absences.reduce((sum, a) => sum + a.deductionAmount, 0);

    const branchName = selectedBranch ? branches.find(b => b.branchId === selectedBranch) : null;
    const employeeName = selectedEmployee ? employees.find(e => e.employeeId === selectedEmployee) : null;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;

    printWindow.document.write(`
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${t.reportTitle}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #333; font-size: 12px; }
          .header { text-align: center; border-bottom: 3px solid #e91e63; padding-bottom: 15px; margin-bottom: 15px; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .header h2 { color: #e91e63; margin: 5px 0; }
          .header .subtitle { font-size: 11px; color: #666; }
          .report-title { text-align: center; margin-bottom: 15px; }
          .report-title h3 { color: #e91e63; font-size: 20px; }
          .filters-box { border: 1px solid #ddd; border-radius: 6px; padding: 10px 15px; margin-bottom: 15px; background: #fce4ec; }
          .filters-box span { font-size: 12px; margin-${isRTL ? 'left' : 'right'}: 15px; }
          .filters-box strong { color: #e91e63; }
          .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 15px; }
          .summary-card { border: 2px solid #e91e63; border-radius: 8px; padding: 12px; text-align: center; }
          .summary-card .value { font-size: 22px; font-weight: bold; color: #e91e63; }
          .summary-card .label { font-size: 11px; color: #666; margin-top: 3px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          thead th { background: #e91e63; color: white; padding: 8px 6px; font-size: 11px; text-align: ${isRTL ? 'right' : 'left'}; }
          thead th.center { text-align: center; }
          thead th.amount { text-align: ${isRTL ? 'left' : 'right'}; }
          tbody td { padding: 6px; border-bottom: 1px solid #eee; font-size: 11px; }
          tbody td.center { text-align: center; }
          tbody td.amount { text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; }
          tbody tr:nth-child(even) { background: #fce4ec; }
          .status-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: bold; color: white; }
          .total-row td { background: #e91e63; color: white; font-weight: bold; font-size: 12px; padding: 8px 6px; }
          .employee-name { font-weight: bold; }
          .employee-code { font-size: 10px; color: #888; }
          .signatures { display: flex; justify-content: space-between; margin-top: 50px; }
          .sig-block { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 50px; font-weight: bold; font-size: 12px; }
          .print-date { text-align: ${isRTL ? 'left' : 'right'}; font-size: 10px; color: #999; margin-bottom: 10px; }
          @media print { @page { size: A4 landscape; margin: 0.8cm; } }
        </style>
      </head>
      <body>
        <div class="print-date">${isRTL ? 'تاريخ الطباعة:' : 'Print Date:'} ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>

        ${companyInfo ? `
        <div class="header">
          ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
          <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
          <div class="subtitle">
            ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
          </div>
        </div>
        ` : ''}

        <div class="report-title">
          <h3>${t.reportTitle}</h3>
        </div>

        ${(selectedBranch || selectedEmployee || fromDate || toDate || statusFilter) ? `
        <div class="filters-box">
          ${branchName ? `<span><strong>${t.branch}:</strong> ${isRTL ? branchName.branchNameAr : branchName.branchName}</span>` : ''}
          ${employeeName ? `<span><strong>${t.employee}:</strong> ${isRTL ? employeeName.fullNameAr : employeeName.fullName}</span>` : ''}
          ${fromDate ? `<span><strong>${t.fromDate}:</strong> ${fromDate}</span>` : ''}
          ${toDate ? `<span><strong>${t.toDate}:</strong> ${toDate}</span>` : ''}
          ${statusFilter ? `<span><strong>${t.status}:</strong> ${getStatusLabel(statusFilter)}</span>` : ''}
        </div>
        ` : ''}

        <div class="summary-grid">
          <div class="summary-card">
            <div class="value">${absences.length}</div>
            <div class="label">${t.count}</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalDays}</div>
            <div class="label">${t.totalDays}</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalDeduction.toFixed(3)}</div>
            <div class="label">${t.totalDeduction} (KD)</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${t.employee}</th>
              <th>${t.branch}</th>
              <th>${t.absenceDate}</th>
              <th>${t.absenceType}</th>
              <th class="center">${t.days}</th>
              <th class="center">${t.hours}</th>
              <th class="amount">${t.deduction}</th>
              <th>${t.reason}</th>
              <th class="center">${t.status}</th>
            </tr>
          </thead>
          <tbody>
            ${absences.map((a, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>
                <span class="employee-name">${isRTL ? a.employeeNameAr : a.employeeName}</span>
                <br/><span class="employee-code">${a.employeeCode}</span>
              </td>
              <td>${a.branchName || '-'}</td>
              <td>${new Date(a.absenceDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</td>
              <td>${getAbsenceTypeLabel(a.absenceType)}</td>
              <td class="center">${a.days}</td>
              <td class="center">${a.hours}</td>
              <td class="amount">${a.deductionAmount.toFixed(3)}</td>
              <td>${a.reason || '-'}</td>
              <td class="center"><span class="status-badge" style="background:${getStatusPrintColor(a.status)}">${getStatusLabel(a.status)}</span></td>
            </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="5" style="text-align: ${isRTL ? 'left' : 'right'};">${isRTL ? 'الإجمالي' : 'Total'}</td>
              <td class="center">${totalDays}</td>
              <td class="center">${totalHours}</td>
              <td class="amount">${totalDeduction.toFixed(3)} KD</td>
              <td colspan="2"></td>
            </tr>
          </tbody>
        </table>

        <div class="signatures">
          <div class="sig-block"><div class="sig-line">${isRTL ? 'مدير الموارد البشرية' : 'HR Manager'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير العام' : 'General Manager'}</div></div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // printWindow stays open for user review
    }, 500);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t.title}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={isRTL ? 'تحديث' : 'Refresh'}>
            <IconButton onClick={loadData} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} color="secondary">{t.print}</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedAbsence(null); setDialogOpen(true); }}>{t.addNew}</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Autocomplete
            options={branches}
            getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
            value={branches.find(b => b.branchId === selectedBranch) || null}
            onChange={(_, newValue) => setSelectedBranch(newValue?.branchId || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} size="small" />}
            size="small"
          />
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
            value={employees.find(e => e.employeeId === selectedEmployee) || null}
            onChange={(_, newValue) => setSelectedEmployee(newValue?.employeeId || '')}
            renderInput={(params) => <TextField {...params} label={t.employee} size="small" />}
            size="small"
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={fromDate ? new Date(fromDate) : null}
              onChange={(date) => {
                const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFromDate(newVal);
                if (toDate && newVal && toDate < newVal) setToDate('');
              }}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={toDate ? new Date(toDate) : maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={toDate ? new Date(toDate) : null}
              onChange={(date) => {
                const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setToDate(newVal);
                if (fromDate && newVal && fromDate > newVal) setFromDate('');
              }}
              format="dd/MM/yyyy"
              minDate={fromDate ? new Date(fromDate) : minDate}
              maxDate={maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
          <TextField select label={t.status} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small">
            <MenuItem value="">{t.all}</MenuItem>
            <MenuItem value="Pending">{t.pending}</MenuItem>
            <MenuItem value="Approved">{t.approved}</MenuItem>
            <MenuItem value="Rejected">{t.rejected}</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={handleSearch} fullWidth>{t.search}</Button>
            <Button variant="outlined" onClick={() => { setSelectedBranch(''); setSelectedEmployee(''); setFromDate(''); setToDate(''); setStatusFilter(''); loadData(); }} fullWidth>{t.clear}</Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t.employee}</TableCell>
              <TableCell>{t.absenceDate}</TableCell>
              <TableCell>{t.absenceType}</TableCell>
              <TableCell align="center">{t.days}</TableCell>
              <TableCell align="center">{t.hours}</TableCell>
              <TableCell align="right">{t.deduction}</TableCell>
              <TableCell>{t.reason}</TableCell>
              <TableCell>{t.status}</TableCell>
              <TableCell align="center">{t.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {absences.map((absence) => {
              const isApplied = absence.isApplied;
              const isLocked = isApplied && !isSuperAdmin;
              return (
              <TableRow key={absence.absenceId} sx={isApplied ? { opacity: isSuperAdmin ? 1 : 0.6, bgcolor: 'action.hover' } : undefined}>
                <TableCell>{isRTL ? absence.employeeNameAr : absence.employeeName}</TableCell>
                <TableCell>{new Date(absence.absenceDate).toLocaleDateString('en-GB')}</TableCell>
                <TableCell>{getAbsenceTypeLabel(absence.absenceType)}</TableCell>
                <TableCell align="center">{absence.days}</TableCell>
                <TableCell align="center">{absence.hours}</TableCell>
                <TableCell align="right">{absence.deductionAmount.toFixed(3)} KD</TableCell>
                <TableCell>{absence.reason}</TableCell>
                <TableCell>
                  {isApplied ? (
                    <Tooltip title={`${isRTL ? 'تم خصمه في كشف رواتب' : 'Applied in payroll'}: ${(absence as any).appliedInPayrollNumber || ''}`}>
                      <Chip label={`${t.applied} — ${(absence as any).appliedInPayrollNumber || ''}`} color="info" size="small" variant="filled" icon={<LockIcon />} />
                    </Tooltip>
                  ) : absence.status === 'Acknowledged' && absence.opsManagerAcknowledged ? (
                    <Tooltip title={`${isRTL ? 'وافق عليها' : 'Acknowledged by'}: ${(absence as any).opsManagerName || ''} - ${absence.opsManagerDate ? new Date(absence.opsManagerDate).toLocaleDateString('en-GB') : ''}`}>
                      <Chip label={`${getStatusLabel(absence.status)} - ${(absence as any).opsManagerName || ''}`} color="warning" size="small" />
                    </Tooltip>
                  ) : (
                    <Chip label={getStatusLabel(absence.status)} color={getStatusColor(absence.status)} size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  {!isLocked && (
                    <>
                      {/* Ops Manager acknowledge — only if no APPROVE permission */}
                      {absence.status === 'Pending' && !absence.opsManagerAcknowledged && permissions?.includes('ABSENCE_ACKNOWLEDGE') && !permissions?.includes('ABSENCE_APPROVE') && (
                        <Tooltip title={isRTL ? 'موافقة أولية' : 'Acknowledge'}>
                          <IconButton size="small" color="warning" onClick={() => handleAcknowledge(absence.absenceId)}><CheckIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      {/* Final approve — only HR/SuperAdmin with ABSENCE_APPROVE */}
                      {(absence.status === 'Pending' || absence.status === 'Acknowledged') && permissions?.includes('ABSENCE_APPROVE') && (
                        <>
                          <Tooltip title={isRTL ? 'اعتماد نهائي' : 'Approve'}>
                            <IconButton size="small" color="success" onClick={() => handleApprove(absence.absenceId, 'Approved')}><CheckIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title={isRTL ? 'رفض' : 'Reject'}>
                            <IconButton size="small" color="error" onClick={() => handleApprove(absence.absenceId, 'Rejected')}><CloseIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                      {/* Edit/Delete — Pending or Acknowledged, by creator */}
                      {(absence.status === 'Pending' || absence.status === 'Acknowledged') && canEditAbsence(absence.branchId) && (
                        <>
                          <IconButton size="small" onClick={() => { setSelectedAbsence(absence); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(absence.absenceId)}><DeleteIcon fontSize="small" /></IconButton>
                        </>
                      )}
                    </>
                  )}
                  {isSuperAdmin && (absence.status === 'Approved' || isApplied) && (
                    <>
                      <IconButton size="small" onClick={() => { setSelectedAbsence(absence); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(absence.absenceId)}><DeleteIcon fontSize="small" /></IconButton>
                    </>
                  )}
                  <AttachmentSection documentType="Absence" documentId={absence.absenceId} isRTL={isRTL} />
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <AbsenceDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setSelectedAbsence(null); }}
        onSave={loadData}
        absence={selectedAbsence}
      />
    </Box>
  );
};

export default AbsencesPage;